<template>
   <div id="task-request-list">
      <div class="group-page-header">
         <h1 class="group-page-title">{{ $t("taskRequestList_title") }}</h1>
         <p class="group-page-subtitle">{{ $t("taskRequestList_subtitle") }}</p>
      </div>

      <TaskRequestTableControls :total-count="totalCount" :filter-options="subStatusOptions" @filter-change="onFilterChange" @search="onSearch" />

      <div class="task-table">
         <div class="task-table__header">
            <div v-for="col in columns" :key="col.key" class="table-header-cell">
               <span>{{ col.label }}</span>
               <Icon v-if="col.sortable" name="chevron-selector-vertical" />
            </div>
         </div>

         <div v-if="isLoading" class="task-table__empty">{{ $t("taskRequestList_loading") }}</div>

         <div v-else-if="paginatedTasks.length === 0" class="task-table__empty">{{ $t("taskRequestList_noTasksFound") }}</div>

         <TaskRequestTableRow v-for="(task, index) in paginatedTasks" :key="task.id" :task="task" :is-alt="index % 2 !== 0" />
      </div>

      <TaskRequestPagination :current-page="currentPage" :total-pages="totalPages" @page-change="currentPage = $event" />
   </div>
</template>

<script>
import moment from "moment";
import { listTasks } from "@/service/task.api.js";
import Icon from "@/components/atoms/Icon.vue";
import TaskRequestTableControls from "@/components/task/request-list/TaskRequestTableControls.vue";
import TaskRequestTableRow from "@/components/task/request-list/TaskRequestTableRow.vue";
import TaskRequestPagination from "@/components/task/request-list/TaskRequestPagination.vue";

const PER_PAGE = 10;

export default {
   name: "TaskRequestList",
   components: {
      Icon,
      TaskRequestTableControls,
      TaskRequestTableRow,
      TaskRequestPagination,
   },
   data() {
      return {
         tasks: [],
         isLoading: false,
         searchText: "",
         selectedSubStatus: -1,
         currentPage: 1,
         columns: [],
      };
   },
   computed: {
      subStatusOptions() {
         return [
            { value: 1, label: this.$t("taskRequestList_statusNew") },
            { value: 2, label: this.$t("taskRequestList_statusInProgress") },
            { value: 3, label: this.$t("taskRequestList_statusWaitingResponse") },
            { value: 6, label: this.$t("taskRequestList_statusClosed") },
            { value: 7, label: this.$t("taskRequestList_statusApproved") },
         ];
      },
      filteredTasks() {
         let result = this.tasks;

         if (this.selectedSubStatus !== -1) {
            result = result.filter((t) => t.subStatus === this.selectedSubStatus);
         }

         if (this.searchText.trim()) {
            const q = this.searchText.toLowerCase();
            result = result.filter((t) => {
               const dep = t.groupPnrDetails?.departureAirport || "";
               const arr = t.groupPnrDetails?.arrivalAirport || "";
               return String(t.id).includes(q) || dep.toLowerCase().includes(q) || arr.toLowerCase().includes(q);
            });
         }

         return result;
      },
      totalCount() {
         return this.filteredTasks.length;
      },
      totalPages() {
         return Math.max(1, Math.ceil(this.filteredTasks.length / PER_PAGE));
      },
      paginatedTasks() {
         const start = (this.currentPage - 1) * PER_PAGE;
         return this.filteredTasks.slice(start, start + PER_PAGE);
      },
   },
   methods: {
      async fetchTasks() {
         this.isLoading = true;
         try {
            const requestModel = {
               orderId: 0,
               // date1: moment().subtract(1, "year").format("YYYY-MM-DD"),
               date1: moment().subtract(2, "days").format("YYYY-MM-DD"),
               // date1: moment().format("YYYY-MM-DD"),
               date2: moment().format("YYYY-MM-DD"),
               type: 12,
               status: -1,
            };
            const response = await listTasks(requestModel);
            if (response?.result?.success) {
               this.tasks = response.tasks || [];
            }
         } catch (error) {
            // Handle error silently
         } finally {
            this.isLoading = false;
         }
      },
      onFilterChange(val) {
         this.selectedSubStatus = parseInt(val);
         this.currentPage = 1;
      },
      onSearch(val) {
         this.searchText = val;
         this.currentPage = 1;
      },
   },
   mounted() {
      this.columns = [
         { key: "id", label: this.$t("taskRequestList_columnRequestId"), sortable: true },
         { key: "route", label: this.$t("taskRequestList_columnRoute"), sortable: true },
         { key: "departureDate", label: this.$t("taskRequestList_columnDepartureDate"), sortable: true },
         { key: "returnDate", label: this.$t("taskRequestList_columnReturnDate"), sortable: true },
         { key: "cabinType", label: this.$t("taskRequestList_columnCabinType"), sortable: true },
         { key: "paxCount", label: this.$t("taskRequestList_columnPassenger"), sortable: false },
         { key: "status", label: this.$t("taskRequestList_columnStatus"), sortable: false },
         { key: "status", label: this.$t("taskRequestList_lastTicketDateTime"), sortable: false },
         // { key: "subStatus", label: this.$t("taskRequestList_columnSubStatus"), sortable: false },
      ];
      this.fetchTasks();
   },
};
</script>

<style lang="scss" scoped>
#task-request-list {
   .task-table {
      padding: 8px 0;

      &__header {
         display: grid;
         grid-template-columns: 1fr 0.75fr 1fr 1fr 0.85fr 0.75fr 1fr 1fr;
         align-items: center;
         padding: 8px 16px;
         background-color: var(--surface-level-2);
         border-radius: 8px;

         .table-header-cell {
            display: inline-flex;
            align-items: center;
            gap: 2px;
            font-size: 14px;
            font-weight: 500;
            color: var(--gray-500);
            white-space: nowrap;

            .ui-icon {
               width: 16px;
               height: 16px;
            }
         }
      }

      &__empty {
         padding: 40px 16px;
         text-align: center;
         color: var(--gray-500);
         font-size: 14px;
      }
   }
}
</style>
